import {
  type Signal,
  type EffectCleanupRegisterFn,
  type WritableSignal,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';

import { type WSPlayer } from '../../models/WS-player.model';
import { type BlockTemplateType } from '../../models/block-template.model';
import { type TrainingSectionType } from '../../models/training-section.model';
import { type Urls, type VideoInformation } from '../../models/video-information.model';
import { type WSUIConfig } from '../../models/WS-ui-config.model';
import { type WSBroadcastDataMessage } from '../../models/WSMessages/WS-broadcast-data-message.model';

import { Rank } from '../../components/rank/rank';
import { ScreenDatas } from '../../components/screen-datas/screen-datas';
import { ScreenVideo } from '../../components/screen-video/screen-video';
import { ScreenChart } from '../../components/screen-chart/screen-chart';
import { ApiService } from '../../services/api-service';
import { WebSocketService } from '../../services/websocket-service';
import { Resistance } from '../../components/resistance/resistance';
import { ScreenMap } from '../../components/screen-map/screen-map';
import { DisplayModeDirective } from '../../directives/display-mode.directive';
import { LoaderOverlay } from '../../components/loader-overlay/loader-overlay';
import { Icon } from '../../components/icon/icon';
import { PauseOverlay } from '../../components/pause-overlay/pause-overlay';
import { LeafletService } from '../../services/leaflet-service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-scenic-display-page',
  imports: [
    Rank,
    ScreenDatas,
    ScreenVideo,
    ScreenChart,
    ScreenMap,
    Resistance,
    DisplayModeDirective,
    LoaderOverlay,
    Icon,
    PauseOverlay,
    TranslateModule,
  ],
  templateUrl: './scenic-display-page.html',
  styleUrl: './scenic-display-page.scss',
  host: {
    '[style.--bg-image]': '`url(${videoInformations()?.thumbnails?.xlarge })`',
  },
})
export class ScenicDisplayPage {
  private static readonly DISPLAY_SWITCH_DELAY_MS: number = 300;

  private readonly apiService: ApiService = inject(ApiService);
  private readonly leafletService: LeafletService = inject(LeafletService);
  private readonly webSocketService: WebSocketService = inject(WebSocketService);

  dataIsLoaded: Signal<boolean> = computed<boolean>(() => {
    return this.videoInformations() !== undefined && this.trainingSections() !== undefined;
  });

  protected readonly videoInformations: Signal<VideoInformation | undefined> =
    this.apiService.scenicInformation;
  protected readonly trainingSections: Signal<TrainingSectionType[] | undefined> =
    this.apiService.scenicTrainingSections;

  protected readonly videoQuality: Signal<keyof Urls> = computed<keyof Urls>(() => {
    if (!this.webSocketService.config()?.videoQuality) return 'sd';
    return this.webSocketService.config()?.videoQuality === 'auto'
      ? 'hls'
      : (this.webSocketService.config()?.videoQuality as keyof Urls);
  });
  protected readonly videoUrl: Signal<string | undefined> = computed<string | undefined>(
    () => this.videoInformations()?.urls[this.videoQuality()],
  );
  private readonly config: Signal<WSUIConfig | null> = this.webSocketService.config;
  private readonly broadcastData: Signal<WSBroadcastDataMessage | null> =
    this.webSocketService.broadcastData;
  readonly players: Signal<WSPlayer[]> = computed(() => this.broadcastData()?.players ?? []);
  readonly dataBlocks: Signal<BlockTemplateType[]> = computed(
    () => this.config()?.blocksTemplate ?? [],
  );
  readonly isImmerssiveMode: Signal<boolean> = computed(() => this.config()?.immersion ?? false);
  readonly chartEnabled: Signal<boolean> = computed(() => this.config()?.chart !== 'hide');
  readonly miniMapEnabled: Signal<boolean> = computed(() => this.config()?.map !== 'hide');

  readonly tilesDisplayable: Signal<boolean> = this.leafletService.tilesIsDisplayable;

  readonly rankEnabled: Signal<boolean> = computed(
    () =>
      this.config()?.rank !== 'hide' &&
      this.webSocketService.contentInformation()?.trainingMode === 'variableSpeed',
  );
  readonly datasEnabled: Signal<boolean> = computed(() => this.config()?.blocks !== 'hide');
  readonly resistanceBlockEnabled: Signal<boolean> = computed(
    () => this.config()?.resistance !== 'hide',
  );

  readonly isMiniMap: WritableSignal<boolean> = signal(false);
  readonly isMiniVideo: WritableSignal<boolean> = signal(true);
  readonly hasBegin: Signal<boolean> = this.webSocketService.hasBegin;
  readonly isPlaying: Signal<boolean> = computed(
    () => this.webSocketService.videoStatus() === 'play',
  );

  protected get displaySwitchEnabled(): boolean {
    return true;
  }

  constructor() {
    effect((onCleanup: EffectCleanupRegisterFn) => {
      if (!this.displaySwitchEnabled) {
        this.isMiniVideo.set(false);
        this.isMiniMap.set(false);
        return;
      }

      const [hiddenSignal, shownSignal]: WritableSignal<boolean>[] =
        this.webSocketService.requestedDisplay() === 'video' || this.isImmerssiveMode()
          ? [this.isMiniVideo, this.isMiniMap]
          : [this.isMiniMap, this.isMiniVideo];
      hiddenSignal.set(false);
      const timeoutId: number = window.setTimeout(() => {
        shownSignal.set(true);
      }, ScenicDisplayPage.DISPLAY_SWITCH_DELAY_MS);
      onCleanup(() => window.clearTimeout(timeoutId));
    });
  }
}
